import { Suspense, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { useCurrentKitAndTinyAccount } from 'hooks/useCurrentKitAndTinyAccount';
import useConditions from 'services/conditions/useConditions';
import useActionPlanData from 'services/actionPlan/actionPlanData';
import { ActionPlanDesiredOutcome, ActionPlanItem } from 'types/ActionPlan';
import { ConditionForKit } from 'types/Conditions';
import { FeedbackPageType } from 'types/Feedback';
import Guide from 'types/Guide';

import {
  EvidenceRating,
  Spinfinity,
  FAQ,
  CardSlider,
  TextLink,
  FeedbackWidget,
  Markdown,
  PageContainer,
  Header,
} from 'components';
import { Typography, ContainerSizes } from '@repo/ui';
import { ActionPlanGroup } from 'views/ActionPlan/ActionPlanGroup/ActionPlanGroup';
import { GuideCard } from 'views/Guides/GuideCard/GuideCard';
import { ConditionCard } from './ConditionCard';
import { ConditionRisk } from './ConditionRisk';
import TwoColumnGrid from 'components/TwoColumnGrid/TwoColumnGrid';

import styles from './ConditionDetailView.module.css';
import Breadcrumb from 'components/Breadcrumb/Breadcrumb';

const filterOutcomesBySectionAndId = (
  data: ActionPlanItem,
  section = '',
  filterId = '',
) => {
  const cardData = {
    ...data,
    display_title: data.display_title,
    detail: data.detail,
    desired_outcomes:
      section && filterId
        ? data.desired_outcomes?.filter(
            (outcome: ActionPlanDesiredOutcome) =>
              outcome.section === section && outcome.criteria === filterId,
          )
        : data.desired_outcomes,
    id: data.id,
  };
  return cardData;
};

export const ConditionDetailView = () => {
  const { currentKitId, currentKit } = useCurrentKitAndTinyAccount();
  const { condition } = useParams<{ condition: string }>();
  if (!condition) throw new Error('No condition provided');

  // get the conditions for this kit
  const { data: kitData } = useConditions({
    kitId: currentKitId,
  });

  const currentCondition: ConditionForKit | null = useMemo(() => {
    if (!kitData) return null;
    const numericId = Number(condition);
    return (
      kitData.find((cond: ConditionForKit) => cond.id === numericId) ?? null
    );
  }, [condition, kitData]);

  const { data: actionPlanFromDBResults } = useActionPlanData({
    kitId: currentKitId,
  });
  const actionPlanItemsFiltered = !actionPlanFromDBResults?.length
    ? []
    : actionPlanFromDBResults
        .map((item: ActionPlanItem) =>
          filterOutcomesBySectionAndId(
            item,
            'Biomarkers',
            currentCondition?.id + '',
          ),
        )
        .filter((item: ActionPlanItem) => !!item.desired_outcomes?.length);

  const guidesToShow = useMemo(() => {
    return (
      currentCondition?.guides?.map((guide: Guide) => (
        <GuideCard key={guide.id} guide={guide} />
      )) ?? []
    );
  }, [currentCondition?.guides]);

  if (!currentKitId || !currentCondition) return <Spinfinity />;

  return (
    <>
      <Breadcrumb
        className={styles.breadcrumb}
        overrides={{
          [currentCondition.id ?? '']: currentCondition.display_title,
        }}
      />
      <Header title={currentCondition?.display_title ?? condition} />
      <PageContainer size={ContainerSizes.LG}>
        <TwoColumnGrid>
          <TwoColumnGrid.Wide>
            <div className='flex flex-column gap-4'>
              <ConditionCard
                condition={currentCondition}
                kit={currentKit}
                withEvaluationText
              />
              {(!!currentCondition?.evidence_rating ||
                !!currentCondition?.about) && (
                <div className='flex flex-column gap-2'>
                  <Typography variant='heading-m'>{`About the ${currentCondition?.display_title ?? condition} ${
                    currentCondition?.type === 'biomarker'
                      ? 'Biomarker'
                      : 'Associations'
                  }`}</Typography>
                  {!!currentCondition?.evidence_rating && (
                    <EvidenceRating
                      evidenceRating={currentCondition.evidence_rating}
                    />
                  )}
                  {!!currentCondition?.about && (
                    <Typography
                      variant='body-m'
                      className={styles.secondaryText}
                    >
                      <Markdown>{currentCondition.about}</Markdown>
                    </Typography>
                  )}
                </div>
              )}

              <Suspense fallback={<Spinfinity />}>
                <ConditionRisk
                  condition={currentCondition}
                  kitId={currentKitId}
                />
              </Suspense>

              <FeedbackWidget
                kitId={currentKitId}
                pageType={FeedbackPageType.CONDITION}
                pageId={currentCondition?.id ?? 0}
              />

              {currentCondition?.faq && (
                <div>
                  <Typography variant='heading-m'>
                    Frequently asked questions
                  </Typography>
                  <FAQ faq={currentCondition.faq} />
                </div>
              )}
              <div className='hide-on-desktop'>
                <ActionPlanGroup actionPlanItems={actionPlanItemsFiltered} />
                {!!guidesToShow?.length && (
                  <div
                    className={!actionPlanItemsFiltered?.length ? '' : 'mt-4'}
                  >
                    <div className={styles.guidesRow}>
                      <h3 className={styles.conditionTitle}>
                        Related Articles
                      </h3>
                      <TextLink label='View all' to={`/guides`} />
                    </div>
                    <div className={styles.conditionsSliderContainer}>
                      <CardSlider cards={guidesToShow} />
                    </div>
                  </div>
                )}
              </div>

              {!!currentCondition?.references && (
                <div className='flex flex-column gap-4'>
                  <Typography variant='heading-m'>References</Typography>
                  <div className={styles.references}>
                    <Markdown>{currentCondition?.references}</Markdown>
                  </div>
                </div>
              )}
            </div>
          </TwoColumnGrid.Wide>
          <TwoColumnGrid.Slim isSticky>
            <div className='hide-on-mobile'>
              <ActionPlanGroup actionPlanItems={actionPlanItemsFiltered} />
              {!!guidesToShow?.length && (
                <div className={!actionPlanItemsFiltered?.length ? '' : 'mt-4'}>
                  <div className={styles.guidesRow}>
                    <Typography
                      variant='heading-m'
                      className={styles.conditionTitle}
                    >
                      Related Articles
                    </Typography>
                    <TextLink label='View all' to={`/guides`} />
                  </div>
                  <div className={styles.guidesGridListContainer}>
                    {guidesToShow}
                  </div>
                </div>
              )}
            </div>
          </TwoColumnGrid.Slim>
        </TwoColumnGrid>
      </PageContainer>
    </>
  );
};
